import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiClient } from '../apiTools/apiClient';
import { allRoutes } from '../constants/config';

const { API_LITGROUP_PROTOTYPES, API_SSE_ENDPOINT } = { ...allRoutes };

// Step labels for display
const STEP_LABELS = {
  s3_copy: '📦 Copying files',
  pdf_conversion: '📄 Converting PDF',
  ai_processing: '🤖 AI Analysis',
  story_import: '📥 Importing story',
  speech_samples: '🎭 Creating speech samples',
  opportunities: '🎯 Creating opportunities',
  complete: '✅ Complete',
};

// ============================================================
// Publishing Phase Constants
// ============================================================
export const PUBLISH_PHASES = {
  SELECTING: 'selecting',       // User selecting prototypes
  CONFIRMING: 'confirming',     // User reviewing before launch
  PUBLISHING: 'publishing',     // Processing in progress
  PAUSED: 'paused',             // User paused
  COMPLETE: 'complete',         // All done
  ERROR: 'error',               // Fatal error
};

export const ITEM_STATUS = {
  QUEUED: 'queued',
  CREATING_LITGROUP: 'creating_litgroup',
  COPYING_FILES: 'copying_files',
  PROCESSING_PDFS: 'processing_pdfs',
  EXTRACTING_CHARACTERS: 'extracting_characters',
  COMPLETE: 'complete',
  ERROR: 'error',
  CANCELLED: 'cancelled',
};

// ============================================================
// Initial State
// ============================================================
const initialState = {
  phase: PUBLISH_PHASES.SELECTING,
  error: null,

  // Available prototypes (fetched from API)
  availablePrototypes: [],
  availableLoading: false,
  availableError: null,

  // Selected prototype UUIDs (checkboxes)
  selectedUuids: [],

  // Publishing queue — each item tracks a single prototype's processing
  publishQueue: [],
  // e.g., { uuid, name, authorName, genre, bookCount, status, progress, error, createdLitGroupUuid }

  currentPublishingIndex: -1,
  isPaused: false,
  isCancelled: false,

  // Progress tracking
  progress: {
    currentStep: '',
    overallPercent: 0,
    messages: [],
  },
};

// ============================================================
// Async Thunks
// ============================================================

/**
 * Fetch all prototypes with status 'ready' for the publish selection screen.
 * Reuses the existing getPrototypes endpoint and filters client-side.
 */
export const fetchReadyPrototypes = createAsyncThunk(
  'prototypePublish/fetchReadyPrototypes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(API_LITGROUP_PROTOTYPES);
      const allPrototypes = response.data.prototypes || [];
      // Filter to only 'ready' status prototypes
      return allPrototypes.filter(p => p.status === 'ready');
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

/**
 * Publish a single prototype — calls the server endpoint that creates 
 * a LitGroup from a prototype (copies S3 files, creates Neo4j entities).
 * This is the atomic unit of the batch pipeline.
 */
export const publishSinglePrototype = createAsyncThunk(
  'prototypePublish/publishSinglePrototype',
  async ({ prototypeUuid, queueIndex }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(updateQueueItemStatus({ index: queueIndex, status: ITEM_STATUS.CREATING_LITGROUP, progress: 10 }));
      dispatch(addProgressMessage(`Creating LitGroup from prototype...`));

      // The server now returns EARLY (202): it creates the LitGroup + Book nodes, marks the
      // prototype 'complete', and runs the heavy per-book import in the BACKGROUND. Completion is
      // signaled by a PUBLISH_COMPLETE SSE (awaited in runPublishPipeline, keyed by prototypeUuid),
      // so we no longer hold a ~1hr request here — a short timeout covers just the fast staging call.
      const response = await apiClient.post(
        `${API_LITGROUP_PROTOTYPES}/${prototypeUuid}/publish`,
        {},
        {},
        { timeout: 120000 } // 2 min — the staging response is near-instant; import runs async
      );

      const result = response.data;

      // Not COMPLETE yet — the import is running in the background. Mark it processing; the
      // orchestrator flips it to COMPLETE when the PUBLISH_COMPLETE SSE arrives.
      dispatch(updateQueueItemStatus({ index: queueIndex, status: ITEM_STATUS.PROCESSING_PDFS, progress: 20 }));
      dispatch(addProgressMessage(`📥 LitGroup "${result.litGroupName || 'Unknown'}" created — importing in background...`));

      return {
        queueIndex,
        litGroupUuid: result.litGroupUuid,
        litGroupName: result.litGroupName,
        booksProcessed: result.booksProcessed || 0,
      };
    } catch (error) {
      const msg = error.response?.data?.error || error.message || 'Publishing failed';
      dispatch(updateQueueItemStatus({ index: queueIndex, status: ITEM_STATUS.ERROR, error: msg }));
      dispatch(addProgressMessage(`❌ Error: ${msg}`));
      return rejectWithValue({ queueIndex, error: msg });
    }
  }
);

/**
 * Master orchestrator: publishes all selected prototypes sequentially.
 * Checks for pause/cancel between each prototype.
 */
export const runPublishPipeline = createAsyncThunk(
  'prototypePublish/runPublishPipeline',
  async (_, { dispatch, getState, rejectWithValue }) => {
    let eventSource = null;
    // Per-prototype completion waiters, keyed by prototypeUuid. Each background import now
    // finishes with a PUBLISH_COMPLETE / PUBLISH_ERROR SSE (see onmessage below); the batch loop
    // registers a waiter and blocks on it, keeping publishing sequential (one import at a time).
    const pending = new Map();

    try {
      const state = getState();
      const { publishQueue } = state.prototypePublish;
      const totalItems = publishQueue.length;

      // Get userId for SSE connection
      const userData = state.user?.data || state.user?.profile?.data || state.user || {};
      const userId = userData.userId || userData.id;

      // Open SSE connection for real-time progress from server pipeline
      if (userId && API_SSE_ENDPOINT) {
        try {
          eventSource = new EventSource(`${API_SSE_ENDPOINT}?userId=${userId}`);
          eventSource.onmessage = (event) => {
            try {
              const parsed = JSON.parse(event.data);
              const data = parsed.data || parsed;

              if (data.type === 'PUBLISH_PROGRESS') {
                const stepLabel = STEP_LABELS[data.step] || data.step;
                const message = data.message || `${stepLabel}...`;

                // Update the progress bar percentage
                if (data.percent !== undefined) {
                  dispatch(updateProgress({
                    currentStep: message,
                    overallPercent: data.percent,
                  }));
                }

                // For AI chunk processing, add per-chunk progress messages
                if (data.step === 'ai_processing' && data.chunkNumber) {
                  dispatch(addProgressMessage(`  🤖 AI chunk ${data.chunkNumber}/${data.totalChunks}`));
                }
              } else if (data.type === 'PUBLISH_COMPLETE') {
                // Background import for this prototype finished — release its waiter.
                const waiter = pending.get(data.prototypeUuid);
                if (waiter) { pending.delete(data.prototypeUuid); waiter.resolve(data); }
              } else if (data.type === 'PUBLISH_ERROR') {
                const waiter = pending.get(data.prototypeUuid);
                if (waiter) { pending.delete(data.prototypeUuid); waiter.reject(new Error(data.message || 'Import failed')); }
              }
            } catch (parseErr) {
              // Ignore non-JSON SSE messages
            }
          };
          eventSource.onerror = () => {
            // SSE errors are non-fatal — progress just won't update in real-time
            console.warn('[PublishPipeline] SSE connection error (non-fatal)');
          };
        } catch (sseErr) {
          console.warn('[PublishPipeline] Failed to open SSE connection:', sseErr);
        }
      }

      dispatch(updateProgress({
        currentStep: `Publishing ${totalItems} prototype(s)...`,
        overallPercent: 5,
      }));

      for (let i = 0; i < totalItems; i++) {
        // Check for cancellation
        const currentState = getState().prototypePublish;
        if (currentState.isCancelled) {
          dispatch(addProgressMessage(`⛔ Publishing cancelled after ${i} of ${totalItems} items.`));
          // Mark remaining as cancelled
          for (let j = i; j < totalItems; j++) {
            dispatch(updateQueueItemStatus({ index: j, status: ITEM_STATUS.CANCELLED }));
          }
          return { completed: i, total: totalItems, cancelled: true };
        }

        // Check for pause — wait loop
        while (getState().prototypePublish.isPaused) {
          await new Promise(resolve => setTimeout(resolve, 500));
          // Re-check cancellation while paused
          if (getState().prototypePublish.isCancelled) {
            dispatch(addProgressMessage(`⛔ Publishing cancelled while paused.`));
            for (let j = i; j < totalItems; j++) {
              dispatch(updateQueueItemStatus({ index: j, status: ITEM_STATUS.CANCELLED }));
            }
            return { completed: i, total: totalItems, cancelled: true };
          }
        }

        const item = publishQueue[i];
        dispatch(setCurrentPublishingIndex(i));
        dispatch(updateProgress({
          currentStep: `Publishing ${i + 1}/${totalItems}: "${item.name}"`,
          overallPercent: Math.round(5 + (90 * i / totalItems)),
        }));

        try {
          // Fire the (now fast) publish call — creates the LitGroup and kicks off the
          // background import. Then wait for this prototype's PUBLISH_COMPLETE over SSE so the
          // batch stays sequential (one book-import at a time) without holding a long request.
          await dispatch(publishSinglePrototype({
            prototypeUuid: item.uuid,
            queueIndex: i,
          })).unwrap();

          const summary = await new Promise((resolve, reject) => {
            const timer = setTimeout(() => {
              if (pending.has(item.uuid)) {
                pending.delete(item.uuid);
                reject(new Error('Timed out waiting for the import to finish (it may still complete on the server).'));
              }
            }, 5400000); // 90-min backstop in case the SSE completion is missed
            pending.set(item.uuid, {
              resolve: (v) => { clearTimeout(timer); resolve(v); },
              reject: (e) => { clearTimeout(timer); reject(e); },
            });
          });

          // Import finished — mark complete and report the pipeline summary.
          dispatch(updateQueueItemStatus({ index: i, status: ITEM_STATUS.COMPLETE, progress: 100 }));
          const pipeline = summary && summary.summary;
          if (pipeline) {
            if (pipeline.booksProcessed > 0) dispatch(addProgressMessage(`  📖 ${pipeline.booksProcessed} book(s) processed`));
            if (pipeline.charactersExtracted > 0) dispatch(addProgressMessage(`  🎭 ${pipeline.charactersExtracted} character(s) extracted`));
            if (pipeline.opportunitiesCreated > 0) dispatch(addProgressMessage(`  🎯 ${pipeline.opportunitiesCreated} opportunity/ies created`));
            if (pipeline.errors && pipeline.errors.length > 0) pipeline.errors.forEach(e => dispatch(addProgressMessage(`  ⚠️ ${e}`)));
          }
          dispatch(addProgressMessage(`✅ "${item.name}" finished importing`));
        } catch (err) {
          // POST failure or a failed/timed-out background import — mark and continue to next.
          dispatch(updateQueueItemStatus({ index: i, status: ITEM_STATUS.ERROR, error: err.message }));
          dispatch(addProgressMessage(`⚠️ "${item.name}": ${err.message || 'failed'}`));
        }
      }

      dispatch(updateProgress({
        currentStep: 'All prototypes processed!',
        overallPercent: 100,
      }));

      const finalState = getState().prototypePublish;
      const completed = finalState.publishQueue.filter(q => q.status === ITEM_STATUS.COMPLETE).length;
      const failed = finalState.publishQueue.filter(q => q.status === ITEM_STATUS.ERROR).length;

      return { completed, failed, total: totalItems, cancelled: false };
    } catch (error) {
      return rejectWithValue(error.message || 'Pipeline failed');
    } finally {
      // Always close the SSE connection when the pipeline ends
      if (eventSource) {
        eventSource.close();
        eventSource = null;
      }
    }
  }
);

// ============================================================
// Slice
// ============================================================
const prototypePublishSlice = createSlice({
  name: 'prototypePublish',
  initialState,
  reducers: {
    // Toggle selection of a prototype UUID
    togglePrototypeSelection: (state, action) => {
      const uuid = action.payload;
      const idx = state.selectedUuids.indexOf(uuid);
      if (idx === -1) {
        state.selectedUuids.push(uuid);
      } else {
        state.selectedUuids.splice(idx, 1);
      }
    },

    // Select all / deselect all
    selectAllPrototypes: (state) => {
      state.selectedUuids = state.availablePrototypes.map(p => p.uuid);
    },
    deselectAllPrototypes: (state) => {
      state.selectedUuids = [];
    },

    // Set phase
    setPublishPhase: (state, action) => {
      state.phase = action.payload;
    },

    // Build the publish queue from selected prototypes
    buildPublishQueue: (state) => {
      state.publishQueue = state.availablePrototypes
        .filter(p => state.selectedUuids.includes(p.uuid))
        .map(p => ({
          uuid: p.uuid,
          name: p.name,
          authorName: p.authorName || '',
          genre: p.genre || '',
          litGroupType: p.litGroupType || 'series',
          bookCount: p.bookCount || 0,
          coversCount: p.coversCount || 0,
          status: ITEM_STATUS.QUEUED,
          progress: 0,
          error: null,
          createdLitGroupUuid: null,
        }));
      state.currentPublishingIndex = -1;
      state.isPaused = false;
      state.isCancelled = false;
      state.progress = { currentStep: '', overallPercent: 0, messages: [] };
    },

    // Update a queue item's status
    updateQueueItemStatus: (state, action) => {
      const { index, status, progress, error } = action.payload;
      if (state.publishQueue[index]) {
        state.publishQueue[index].status = status;
        if (progress !== undefined) state.publishQueue[index].progress = progress;
        if (error !== undefined) state.publishQueue[index].error = error;
      }
    },

    setCurrentPublishingIndex: (state, action) => {
      state.currentPublishingIndex = action.payload;
    },

    // Pause / Resume / Cancel
    pausePublishing: (state) => {
      state.isPaused = true;
    },
    resumePublishing: (state) => {
      state.isPaused = false;
    },
    cancelPublishing: (state) => {
      state.isCancelled = true;
      state.isPaused = false; // unpause so the loop can exit
    },

    // Progress tracking
    updateProgress: (state, action) => {
      Object.assign(state.progress, action.payload);
    },
    addProgressMessage: (state, action) => {
      state.progress.messages.push({
        timestamp: new Date().toISOString(),
        message: action.payload,
      });
    },

    // Full reset
    resetPublishing: (state) => {
      return { ...initialState };
    },
  },

  extraReducers: (builder) => {
    // fetchReadyPrototypes
    builder
      .addCase(fetchReadyPrototypes.pending, (state) => {
        state.availableLoading = true;
        state.availableError = null;
      })
      .addCase(fetchReadyPrototypes.fulfilled, (state, action) => {
        state.availableLoading = false;
        state.availablePrototypes = action.payload;
      })
      .addCase(fetchReadyPrototypes.rejected, (state, action) => {
        state.availableLoading = false;
        state.availableError = action.payload;
      });

    // runPublishPipeline
    builder
      .addCase(runPublishPipeline.pending, (state) => {
        state.phase = PUBLISH_PHASES.PUBLISHING;
      })
      .addCase(runPublishPipeline.fulfilled, (state, action) => {
        state.phase = action.payload?.cancelled ? PUBLISH_PHASES.SELECTING : PUBLISH_PHASES.COMPLETE;
      })
      .addCase(runPublishPipeline.rejected, (state, action) => {
        state.phase = PUBLISH_PHASES.ERROR;
        state.error = action.payload;
      });

    // publishSinglePrototype — update createdLitGroupUuid on success
    builder
      .addCase(publishSinglePrototype.fulfilled, (state, action) => {
        const { queueIndex, litGroupUuid } = action.payload;
        if (state.publishQueue[queueIndex]) {
          state.publishQueue[queueIndex].createdLitGroupUuid = litGroupUuid;
        }
      });
  },
});

// ============================================================
// Export actions
// ============================================================
export const {
  togglePrototypeSelection,
  selectAllPrototypes,
  deselectAllPrototypes,
  setPublishPhase,
  buildPublishQueue,
  updateQueueItemStatus,
  setCurrentPublishingIndex,
  pausePublishing,
  resumePublishing,
  cancelPublishing,
  updateProgress,
  addProgressMessage,
  resetPublishing,
} = prototypePublishSlice.actions;

// ============================================================
// Selectors
// ============================================================
export const selectPublishPhase = (state) => state.prototypePublish.phase;
export const selectAvailablePrototypes = (state) => state.prototypePublish.availablePrototypes;
export const selectAvailableLoading = (state) => state.prototypePublish.availableLoading;
export const selectSelectedUuids = (state) => state.prototypePublish.selectedUuids;
export const selectPublishQueue = (state) => state.prototypePublish.publishQueue;
export const selectPublishProgress = (state) => state.prototypePublish.progress;
export const selectIsPaused = (state) => state.prototypePublish.isPaused;
export const selectPublishError = (state) => state.prototypePublish.error;

// ============================================================
// Export
// ============================================================
export default prototypePublishSlice.reducer;
